import { createContext, useContext } from "react"

export type AnchorItem = { sectionKey: string; title: string }

type AnchorItemContextValue = {
  title: string
  sectionKey: string
  onNavigate: () => void
}

export const AnchorItemContext = createContext<AnchorItemContextValue | null>(
  null,
)

export const useAnchorItemContext = () => {
  const anchorSectionContextValue = useContext(AnchorItemContext)
  if (!anchorSectionContextValue) {
    throw new Error(
      "useAnchorSectionContext must be used within an AnchorItemProvider",
    )
  }
  return anchorSectionContextValue
}

type AnchorContextValue = {
  items: AnchorItem[]
  registerItem: (item: AnchorItem) => void
  unregisterItem: (key: string) => void
  onNavigate: (key: string) => void
  activeKey?: string
}

export const AnchorContext = createContext<AnchorContextValue | null>(null)

export const useAnchorContext = () => {
  const anchorContext = useContext(AnchorContext)
  if (!anchorContext) {
    throw new Error("AnchorContext must be used within an Anchor component")
  }
  return anchorContext
}

type ScrollingAnchorContextValue = {
  items: AnchorItem[]
  registerItem: (item: AnchorItem) => void
  unregisterItem: (key: string) => void
  registerItemElement: (sectionKey: string, element: Element) => void
  unregisterItemElement: (sectionKey: string) => void
  visibilityRootRef: React.RefCallback<Element | null>
  onNavigate: (key: string, onAnchorNavigate: () => void) => void
}

export const ScrollingAnchorContext =
  createContext<ScrollingAnchorContextValue | null>(null)

export const useScrollingAnchorContext = () => {
  const scrollingAnchorContext = useContext(ScrollingAnchorContext)
  if (!scrollingAnchorContext) {
    throw new Error("AnchorContext must be used within an Anchor component")
  }
  return scrollingAnchorContext
}

type ScrollingAnchorActiveKeyContextValue = {
  activeKey?: string
}

export const ScrollingAnchorActiveKeyContext =
  createContext<ScrollingAnchorActiveKeyContextValue | null>(null)

export const useScrollingAnchorActiveKeyContext = () => {
  const scrollingAnchorActiveKeyContext = useContext(
    ScrollingAnchorActiveKeyContext,
  )
  if (!scrollingAnchorActiveKeyContext) {
    throw new Error(
      "AnchorActiveKeyContext must be used within an Anchor component",
    )
  }
  return scrollingAnchorActiveKeyContext
}

type ScrollingAnchorItemContextValue = {
  title: string
  sectionKey: string
  onNavigate: () => void
}

export const ScrollingAnchorItemContext =
  createContext<ScrollingAnchorItemContextValue | null>(null)

export const useScrollingAnchorItemContext = () => {
  const anchorSectionContextValue = useContext(ScrollingAnchorItemContext)
  if (!anchorSectionContextValue) {
    throw new Error(
      "useScrollingAnchorSectionContext must be used within a ScrollingAnchorSection",
    )
  }
  return anchorSectionContextValue
}
